import errorUtil from '@/js/utils/errorUtil'

/**
 * The "send me that email again" button, everywhere it appears.
 *
 * The button itself is never disabled by a limit - only while a request is in flight. A control
 * that is grey for a reason the person cannot see reads as broken, and the request it saves costs
 * us nothing: a refused resend is one cheap call that sends no mail. So the button stays pressable,
 * the state is written next to it, and pressing it while a limit holds returns the same answer in a
 * toast - which also re-syncs the countdown with the server.
 *
 * The API allows one verification mail a minute per address, and five a day. Those limits live in
 * `EmailRateLimitService` (event-api) and nowhere else: this module never counts seconds of its
 * own. It takes the wait the API returns — with the answer on success, in the error details on a
 * refusal — and keeps the button honest for exactly that long.
 *
 * Why not a local constant: a number copied here drifts from the one that decides, and the drift
 * shows up as a button that offers a send the API refuses. That is what the page did before.
 *
 * The deadline is kept in `localStorage`, because the old countdown lived in component state and a
 * page reload brought the button straight back. Only a timestamp is stored — no address.
 */

/** Response field carrying the wait. Mirrors `AuthController.FIELD_NEXT_RESEND_AFTER_SECONDS`. */
const RESPONSE_FIELD_NEXT_RESEND_AFTER_SECONDS = 'nextResendAfterSeconds'

/** Error detail carrying the wait. Mirrors `EmailRateLimitService.DETAIL_RETRY_AFTER_SECONDS`. */
const ERROR_DETAIL_RETRY_AFTER_SECONDS = 'seconds'

/**
 * Which limit is holding the button. Not the backend code - the codes are per feature, and the
 * caller passes its own (see `VERIFICATION_RESEND_LIMIT_CODES` in `AuthRepository`). Mirrors
 * `EmailRateLimitService.LimitReason` on the backend.
 */
export const ResendLimitReason = {
  COOLDOWN: 'COOLDOWN',
  DAILY_LIMIT: 'DAILY_LIMIT'
}

/**
 * Above this the remaining time is told, not counted: nobody watches a number tick for hours, and
 * "try again in 43 200 s" is not an answer a person can use.
 */
export const COUNTDOWN_MAX_SECONDS = 15 * 60

/**
 * Where the deadline survives a reload: the moment it ends and which limit set it, so the wording
 * after a reload is still the right one. Never the address it belongs to.
 */
const STORAGE_KEY = 'asport.verification-resend-available-at'

const MILLISECONDS_PER_SECOND = 1000

function readStoredCooldown () {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch (e) {
    // Private windows, blocked site data, or a value from an older shape: the countdown is a
    // convenience, and the API still refuses an early send, so none of this is load-bearing.
    return null
  }
}

function writeStoredCooldown (deadlineMs, reason) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ until: deadlineMs, reason }))
  } catch (e) {
    // See readStoredCooldown.
  }
}

function clearStoredCooldown () {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // See readStoredCooldown.
  }
}

/** What is left of a stored cooldown, or null when nothing is. */
function remainingCooldownFromStorage () {
  const stored = readStoredCooldown()
  if (!stored || !stored.until) {
    return null
  }
  const remaining = Math.ceil((stored.until - Date.now()) / MILLISECONDS_PER_SECOND)
  return remaining > 0 ? { seconds: remaining, reason: stored.reason || ResendLimitReason.COOLDOWN } : null
}

export default {
  data () {
    return {
      /** Seconds until the next send is allowed. 0 means the button is usable. */
      resendCooldownSeconds: 0,
      /** Which limit is holding it, so the wording can differ. Null when nothing is. */
      resendCooldownReason: null,
      resendCooldownTimer: null
    }
  },
  computed: {
    /** True while the API would refuse a send, whatever the reason. */
    isResendOnCooldown () {
      return this.resendCooldownSeconds > 0
    },
    /** A countdown is only shown while it is short enough to be worth watching. */
    showResendCountdown () {
      return this.isResendOnCooldown && this.resendCooldownSeconds <= COUNTDOWN_MAX_SECONDS
        && !this.isResendDailyLimitReached
    },
    /** The daily cap is a different message: waiting a moment will not help. */
    isResendDailyLimitReached () {
      return this.resendCooldownReason === ResendLimitReason.DAILY_LIMIT
    }
  },
  mounted () {
    // A reload must not lose what the person was told. The reason is restored with the deadline:
    // "you have used today's sends" is a different sentence from "wait 43 s", and guessing the
    // first one wrong leaves a button that explains nothing.
    const remaining = remainingCooldownFromStorage()
    if (remaining) {
      this.startResendCooldown(remaining.seconds, remaining.reason)
    }
  },
  beforeDestroy () {
    this.stopResendCountdown()
  },
  methods: {
    /**
     * Hold the button for the wait the API gave us.
     *
     * @param {number} seconds wait reported by the API
     * @param {string} reason which limit reported it, from `ResendLimitReason`
     */
    startResendCooldown (seconds, reason) {
      this.stopResendCountdown()
      if (!seconds || seconds <= 0) {
        return
      }

      this.resendCooldownSeconds = Math.ceil(seconds)
      this.resendCooldownReason = reason
      writeStoredCooldown(Date.now() + this.resendCooldownSeconds * MILLISECONDS_PER_SECOND, reason)

      this.resendCooldownTimer = setInterval(() => {
        this.resendCooldownSeconds -= 1
        if (this.resendCooldownSeconds <= 0) {
          this.clearResendCooldown()
        }
      }, MILLISECONDS_PER_SECOND)
    },
    /** A sent mail says when the next one may be asked for; hold the button until then. */
    applyResendResult (result) {
      const seconds = result && result[RESPONSE_FIELD_NEXT_RESEND_AFTER_SECONDS]
      this.startResendCooldown(seconds, ResendLimitReason.COOLDOWN)
    },
    /**
     * A refused send says the same thing. Holding the button here is the point: without it the
     * page keeps offering a send that keeps being refused.
     *
     * @param {object} limitCodes the caller's `{ cooldown, dailyLimit }` backend error codes
     * @returns {boolean} true when a limit refused it, false for any other failure
     */
    applyResendRefusal (error, limitCodes) {
      // The codes belong to the feature, not to this module: verification and password reset are
      // refused by the same limiter under different names. Missing them is a programming error, and
      // it must be loud - a silent return here would leave the button offering a refused send.
      if (!limitCodes || !limitCodes.cooldown || !limitCodes.dailyLimit) {
        throw new Error('applyResendRefusal needs the caller limit codes, e.g. VERIFICATION_RESEND_LIMIT_CODES')
      }

      const processed = errorUtil.getProcessedError(error)
      const code = processed && processed.code
      const reason = code === limitCodes.cooldown ? ResendLimitReason.COOLDOWN
        : code === limitCodes.dailyLimit ? ResendLimitReason.DAILY_LIMIT
          : null
      if (reason === null) {
        return false
      }

      const details = processed.fields || {}
      this.startResendCooldown(Number(details[ERROR_DETAIL_RETRY_AFTER_SECONDS]), reason)
      return true
    },
    stopResendCountdown () {
      if (this.resendCooldownTimer) {
        clearInterval(this.resendCooldownTimer)
        this.resendCooldownTimer = null
      }
    },
    clearResendCooldown () {
      this.stopResendCountdown()
      this.resendCooldownSeconds = 0
      this.resendCooldownReason = null
      clearStoredCooldown()
    }
  }
}
